import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { IonSpinner, useIonViewDidEnter } from '@ionic/react'
import WorkOrderTableSection from '../components/WorkOrderTableSection'
import {
  MANAGER_DASHBOARD_DEFAULT_FILTER_VALUES,
  MANAGER_DASHBOARD_SECTIONS_CONFIG,
} from '../constants'
import {
  appliedFiltersCount,
  filterRowsByAdvancedFilters,
  filterRowsByWorkOrderNumber,
} from '../filters'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'
import { AdvancedFiltersPanel } from '../components/.index'
import WorkOrderIndex from './WorkOrderIndex'

const propTypes = {
  fetchManagerWorkOrderLists: PropTypes.func.isRequired,
  workOrders: PropTypes.array,
  workOrdersBySectionId: PropTypes.func.isRequired,
}

const TAB_WORK_ORDER_MANAGEMENT = 'work-order-management'
const TAB_VIEW_ALL = 'view-all'

function Dashboard({
  fetchManagerWorkOrderLists,
  workOrders,
  workOrdersBySectionId,
}) {
  const [activeTab, setActiveTab] = useState(TAB_WORK_ORDER_MANAGEMENT)
  // `workOrderSearch` is the single source of truth for both tabs: the input is
  // bound to it and both tabs filter off it, so what is typed always matches
  // what is displayed regardless of the order tabs are clicked in.
  const [workOrderSearch, setWorkOrderSearch] = useState('')
  const [isListsLoading, setIsListsLoading] = useState(
    !workOrders || workOrders.length === 0
  )
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false)
  const [filterValues, setFilterValues] = useState(
    MANAGER_DASHBOARD_DEFAULT_FILTER_VALUES
  )
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const appliedCount = useMemo(
    () => appliedFiltersCount(filterValues),
    [filterValues]
  )

  useIonViewDidEnter(() => {
    setIsListsLoading(true)
    fetchManagerWorkOrderLists().finally(() => {
      if (isMountedRef.current) setIsListsLoading(false)
    })
  }, [fetchManagerWorkOrderLists])

  const isWorkOrderManagementTab = activeTab === TAB_WORK_ORDER_MANAGEMENT

  // Skipped entirely on the View All tab, where the result is unused.
  const sectionsWithRows = useMemo(() => {
    if (!isWorkOrderManagementTab) return []

    return MANAGER_DASHBOARD_SECTIONS_CONFIG.map((section) => {
      const sectionRows = workOrdersBySectionId(section.id)
      const bySearch = filterRowsByWorkOrderNumber(sectionRows, workOrderSearch)
      const filteredRows = filterRowsByAdvancedFilters(
        bySearch,
        filterValues,
        section.id
      )
      return { section, filteredRows }
    })
  }, [
    isWorkOrderManagementTab,
    workOrdersBySectionId,
    workOrderSearch,
    filterValues,
  ])

  const isSearchActive = workOrderSearch.trim().length > 0
  const hasAnyResults = sectionsWithRows.some(
    ({ filteredRows }) => filteredRows.length > 0
  )
  const showNoResults = isSearchActive && !hasAnyResults

  const hasLoadedWorkOrders = Array.isArray(workOrders) && workOrders.length > 0
  const showWorkOrderListLoading = isListsLoading && !hasLoadedWorkOrders

  function renderWorkOrderManagement() {
    if (showWorkOrderListLoading) {
      return (
        <div className="manager-dashboard__loading">
          <IonSpinner name="crescent" aria-label="Loading work orders" />
        </div>
      )
    }

    if (showNoResults) {
      return (
        <p className="manager-dashboard__no-results">
          No orders found matching your search
        </p>
      )
    }

    return sectionsWithRows
      .filter(({ filteredRows }) => filteredRows.length > 0)
      .map(({ section, filteredRows }) => (
        <WorkOrderTableSection
          key={section.id}
          title={section.title}
          columns={section.columns}
          rows={filteredRows}
        />
      ))
  }

  return (
    <div className="manager-dashboard">
      {/* The search input serves both tabs and stays mounted across tab
      switches. The advanced filters apply to Work Order Management only. */}
      <header className="manager-dashboard__toolbar">
        <input
          type="search"
          className="manager-dashboard__search"
          placeholder="Search by work order number..."
          aria-label="Search by work order number"
          value={workOrderSearch}
          onChange={(e) => setWorkOrderSearch(e.target.value)}
        />
        {isWorkOrderManagementTab && (
          <div className="manager-dashboard__toolbar-actions">
            <button
              type="button"
              className="manager-dashboard__icon-btn"
              aria-label="Open advanced filters"
              aria-expanded={isFiltersPanelOpen}
              onClick={() => setIsFiltersPanelOpen(true)}
            >
              <span className="manager-dashboard__filter-icon" aria-hidden>
                ☰
              </span>
            </button>
            <span className="manager-dashboard__filter-label">
              {appliedCount === 0
                ? 'Filter'
                : `${appliedCount} ${
                    appliedCount === 1 ? 'filter' : 'filters'
                  } applied`}
            </span>
            {appliedCount > 0 && (
              <button
                type="button"
                className="manager-dashboard__filter-clear"
                onClick={() =>
                  setFilterValues(MANAGER_DASHBOARD_DEFAULT_FILTER_VALUES)
                }
              >
                Clear
              </button>
            )}
          </div>
        )}
      </header>

      {isWorkOrderManagementTab && (
        <AdvancedFiltersPanel
          isOpen={isFiltersPanelOpen}
          onClose={() => setIsFiltersPanelOpen(false)}
          filterValues={filterValues}
          onFilterChange={setFilterValues}
          appliedCount={appliedCount}
        />
      )}

      <div className="manager-dashboard__heading">
        <h1 className="manager-dashboard__title">Dashboard</h1>
      </div>

      <div className="manager-dashboard__tab-panel-wrap">
        <div
          className="manager-dashboard__tabs"
          role="tablist"
          aria-label="Manager work orders tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={isWorkOrderManagementTab}
            className={`manager-dashboard__tab ${
              isWorkOrderManagementTab ? 'manager-dashboard__tab--active' : ''
            }`}
            onClick={() => setActiveTab(TAB_WORK_ORDER_MANAGEMENT)}
          >
            Work Order Management
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={!isWorkOrderManagementTab}
            className={`manager-dashboard__tab ${
              !isWorkOrderManagementTab ? 'manager-dashboard__tab--active' : ''
            }`}
            onClick={() => setActiveTab(TAB_VIEW_ALL)}
          >
            View All
          </button>
        </div>
        <div className="manager-dashboard__panel">
          {isWorkOrderManagementTab ? (
            renderWorkOrderManagement()
          ) : (
            <WorkOrderIndex search={workOrderSearch} />
          )}
        </div>
      </div>

      {isWorkOrderManagementTab && (
        <footer className="manager-dashboard__footer">
          <strong>Congratulations!</strong> You've reached the end of Work Order
          Management.
        </footer>
      )}
    </div>
  )
}

Dashboard.propTypes = propTypes

function mapStateToProps(state) {
  return {
    workOrders: selectors.workOrders(state),
    workOrdersBySectionId: (sectionId) =>
      selectors.workOrdersBySection(state, sectionId),
  }
}

const mapDispatchToProps = {
  fetchManagerWorkOrderLists: apiActions.fetchManagerWorkOrderLists,
}

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard)
